import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import { isVideoAsset } from "src/pages/ad-studio/briefs/utils";

export type AdAssetPreviewAsset = {
  asset_type?: string | null;
  asset_url?: string | null;
  svg?: string | null;
  component_assets?: Record<string, string> | null;
  /**
   * Both maps are JSON scalars. `urls` keeps the shape `VideoAssetThumbnail`
   * already consumes; `webpUrls` stays `unknown` until
   * `directUrlsForRendition` narrows it.
   */
  component_asset_urls?: {
    urls?: Record<string, string> | null;
    webpUrls?: unknown;
  } | null;
  video_composition?: VideoComposition | null;
};

export type AdAssetKind = "composition" | "video-file" | "static" | "empty";

/**
 * Single source of truth for classifying an ad asset by how it must be rendered.
 * Keyed off the data (composition → video URL → markup/image), NOT `asset_type`,
 * so an exported video that lacks a composition still plays instead of rendering
 * blank, and any asset carrying a composition renders it rather than a placeholder.
 *
 * Lives apart from AdAssetPreview so the classification is unit-testable without
 * pulling in the Remotion renderer.
 */
export function getAdAssetKind(
  asset: AdAssetPreviewAsset | null | undefined,
): AdAssetKind {
  if (!asset) {
    return "empty";
  }
  if (asset.video_composition) {
    return "composition";
  }
  if (isVideoAsset(asset.asset_url)) {
    return "video-file";
  }
  if (asset.svg || asset.asset_url) {
    return "static";
  }
  return "empty";
}
